import {
  CARD_SLOTS,
  type CardLoadout,
  type CardSlot,
  cardCooldownMsAtLevel,
  cardDefinition,
} from '@kenocyber/shared';
import { CARD_CHARGE_FULL_BPS, cardBalance, cardChargeStrengthBps } from './balance';

/**
 * **O estado autoritativo das três cartas de uma corrida** (gate V28H.H).
 *
 * ## Uma fundação genérica, e uma habilidade
 *
 * Os três espaços têm o MESMO estado, seja qual for a carta que lá está: um
 * cooldown que corre, um tempo activo que corre, e um acumulador de disparo.
 * Isso é a fundação. O que difere é o que acontece quando o tempo activo é
 * maior que zero, e hoje só uma carta sabe responder a isso.
 *
 * As outras quinze são INERTES por ausência, e não por um efeito vazio: elas
 * não estão em {@link IMPLEMENTED_CARD_ABILITIES}, portanto nunca activam,
 * nunca gastam cooldown e nunca emitem um evento. Inventar-lhes um efeito de
 * zero seria escrever quinze decisões que ninguém tomou.
 *
 * ## Tudo em milissegundos INTEIROS
 *
 * A caça avança em tiques de 16 ms inteiros, e todos os relógios desta
 * fundação avançam pelo mesmo número. Nunca um relógio de parede: o servidor
 * re-simula a mesma janela e tem de chegar ao mesmo estado, e um `Date.now()`
 * tornaria isso impossível.
 *
 * ## Nada aqui decide arte
 *
 * Este módulo não sabe o que é um sprite, uma escala ou um brilho. Ele decide
 * quando a habilidade está activa e quando ela dispara; a cena lê o estado e
 * desenha.
 */

/**
 * As cartas que TÊM implementação.
 *
 * Uma lista e não uma bandeira por carta: quem não está aqui não activa, e
 * acrescentar uma habilidade é acrescentar um id e o seu ramo em
 * `simulation.ts`. Enquanto ela não estiver aqui, a carta continua a poder ser
 * equipada e a aparecer no inventário - o que ela não faz é nada.
 */
export const IMPLEMENTED_CARD_ABILITIES: readonly string[] = Object.freeze([
  /* Movimento e temporizadores sobre um casco (gate V28H.L). */
  'card_dash',
  'card_speed_burst',
  'card_cloak',
  'card_bastion',
  /* Penalidades sobre um alvo, escolhido pelo servidor. */
  'card_freeze',
  'card_slow',
  'card_attack_break',
  'card_damage_break',
  /* E sobre todos de uma vez. */
  'card_malware',
  /* Uma lâmina que orbita o casco, sem entidade nenhuma. */
  'card_blade_storm',
  /* As duas que põem uma entidade no mundo. */
  'card_attack_drone',
  'card_leviathan',
  /* As que carregam, e as que rebentam. */
  'card_solar_lance',
  'card_charged_fireball',
  'card_catole',
  /* A primeira de todas (gates V28H.H e V28H.J). */
  'card_super_cannons',
]);

export function cardAbilityIsImplemented(definitionId: string): boolean {
  return IMPLEMENTED_CARD_ABILITIES.includes(definitionId);
}

/**
 * Se esta carta tem habilidade escrita NESTE modo.
 *
 * ## Porque a pergunta tem um modo, e continua a ter
 *
 * Ela nasceu (gate V28H.L) porque as duas invocações funcionavam no duelo e não
 * na caça, e houve uma lista de pendentes a dizê-lo. **Essa lista acabou** no
 * gate V28H.M: as dezasseis funcionam exactamente nos modos que o catálogo
 * declara, e o Drone e o Leviatã são entidades nos dois sítios.
 *
 * A função fica, e a razão é a que a fez existir: uma carta futura pode entrar
 * no catálogo com um modo escrito e outro por escrever, e quem pergunta tem de
 * poder perguntar pelo modo em vez de por "existe em algum". Hoje ela responde
 * o mesmo que {@link cardAbilityIsImplemented} para as dezasseis, e é uma prova
 * que o afirma em vez de um comentário.
 */
export function cardAbilityIsImplementedIn(definitionId: string, mode: 'hunt' | 'pvp'): boolean {
  /*
   * O modo não muda a resposta hoje, e a assinatura fica na mesma: ela é o
   * sítio onde uma carta futura escrita só num modo será recusada no outro.
   */
  void mode;
  return cardAbilityIsImplemented(definitionId);
}

/**
 * Quanto tempo esta carta fica ACTIVA depois de disparar, em milissegundos.
 *
 * `0` para uma carta sem implementação: ela nunca activa, portanto nunca chega
 * a perguntar. Para os Super Canhões, seis segundos em todos os níveis - o
 * nível mexe no cooldown e em mais nada, que é a decisão de equilíbrio mais
 * forte deste sistema.
 */
export function cardAbilityDurationMs(definitionId: string): number {
  /*
   * Lida da TABELA e já não de um `if` por carta (gate V28H.L). Com uma
   * habilidade escrita, um `if` era honesto; com dezasseis seria dezasseis
   * sítios onde a décima sétima se esquece de aparecer.
   *
   * Um id que a tabela não conhece devolve `0`, que é a mesma resposta de
   * sempre - e ele nunca chega aqui, porque `cardAbilityIsImplemented` recusa
   * antes.
   */
  /*
   * Zero para uma carta SEM implementação, e é a regra original: ela nunca
   * activa, portanto nunca chega a perguntar - e devolver a duração da tabela
   * faria uma carta inerte anunciar um tempo que ela não tem.
   */
  if (!cardAbilityIsImplemented(definitionId)) {
    return 0;
  }
  return cardBalance(definitionId)?.durationMs ?? 0;
}

/**
 * **A forma dos Super Canhões, num sítio que os dois modos podem ler.**
 *
 * A caça e o duelo têm arenas, unidades e relógios diferentes, e nenhum dos dois
 * pode importar o outro - `pvp/` tem um guarda que o proíbe, e o guarda existe
 * porque uma dependência dessas faria o equilíbrio de um modo mexer no outro.
 *
 * O que é da CARTA - quanto tempo dura, quantos canhões monta, e onde eles ficam
 * em relação ao casco - vive aqui, uma vez. O que é de cada arena - pixels,
 * milli-unidades, escala de arte - fica de cada lado.
 */

/** Seis segundos, em todos os níveis. Contrato aprovado. */
export const SUPER_CANNONS_DURATION_MS = 6_000;

/** Exactamente dois. Contrato aprovado, e o desenho depende disso. */
export const SUPER_CANNONS_COUNT = 2;

/**
 * O afastamento lateral, em basis points da MEIA-ALTURA do casco.
 *
 * Uma proporção e não um número absoluto: a caça desenha um casco de 96 unidades
 * e o duelo um de outro tamanho, e o que o dono aprovou foi a relação - os
 * canhões ficam um pouco para lá da meia-largura da nave.
 *
 * 92 de 48 é 19 166 bps, e é essa a leitura: quase o dobro da meia-altura.
 */
export const SUPER_CANNONS_LATERAL_BPS = 19_166;

/** E o recuo, pela mesma medida: 34 de 48 é 7 083 bps. */
export const SUPER_CANNONS_BACK_BPS = 7_083;

/** O estado de UM espaço, tal como a instantânea o guarda. */
export interface CardAbilityState {
  readonly slot: CardSlot;
  readonly definitionId: string;
  readonly instanceId: string;
  readonly level: number;
  /**
   * Quanto falta até a carta poder voltar a activar, em ms.
   *
   * `0` significa PRONTA. Uma sessão nova nasce com zero nos três espaços: o
   * contrato diz que a carta começa pronta, e não que ela espera um cooldown
   * antes do primeiro uso.
   */
  cooldownRemainingMs: number;
  /**
   * Quanto falta até a habilidade acabar, em ms. `0` significa inactiva.
   *
   * Corre AO MESMO TEMPO que o cooldown, e não depois: o cooldown começa no
   * instante da activação, portanto uma carta de nível 6 - 14 s de cooldown,
   * 6 s activa - fica inactiva durante 8 s e não durante 14.
   */
  activeRemainingMs: number;
  /**
   * O acumulador de disparo DESTA habilidade, em ms.
   *
   * Próprio, e nunca o da nave: partilhá-lo faria a cadência do casco depender
   * de a habilidade estar activa. Decide o futuro - quantos projéteis nascem no
   * próximo tique - portanto vive na instantânea.
   */
  attackAccumulatorMs: number;
  /**
   * **Quanto falta de CARGA, em ms** (gate V28H.L). `0` significa carregada.
   *
   * Duas cartas carregam antes de fazer o que fazem - a Lança Solar e a Esfera
   * Ígnea - e a carga não é o mesmo que a duração: enquanto ela corre, a
   * habilidade está ligada e ainda não produziu nada. A Lança prende o movimento
   * durante a carga, e é a única que o faz, por descrição aprovada.
   *
   * Estado que decide o FUTURO - quando o feixe acende, quando a esfera sai - e
   * portanto atravessa a instantânea. Uma reconexão a meio de uma carga devolve
   * a carga a meio, e não uma esfera já lançada nem uma carga do princípio.
   */
  chargeRemainingMs: number;
  /**
   * **Quanto valeu a carga que foi largada, em bps** (gate V28H.N).
   *
   * A Lança Solar carrega enquanto a tecla está premida, e o que ela produz -
   * espessura, intensidade e dano - cresce com o tempo aguentado. Este é esse
   * número, fixado no INSTANTE em que a tecla se solta e nunca mais mexido: o
   * feixe já aceso não muda de força a meio.
   *
   * Decide o futuro - quanto dano cada parcela cobra, e quem a espessura apanha
   * - portanto atravessa a instantânea. Uma reconexão a meio de um feixe devolve
   * o mesmo feixe, e não um feixe cheio nem um feixe apagado.
   */
  chargeStrengthBps: number;
}

/**
 * O estado inicial dos três espaços de uma sessão NOVA.
 *
 * Prontas, inactivas e com o acumulador a zero, pela ordem canónica de
 * `CARD_SLOTS`. Um espaço vazio não produz entrada nenhuma - a lista tem entre
 * zero e três elementos, e a ordem é a do slot.
 */
export function initialCardAbilities(loadout: CardLoadout | undefined): CardAbilityState[] {
  if (!loadout) {
    return [];
  }
  const order = new Map(CARD_SLOTS.map((slot, index) => [slot, index]));
  return [...loadout.cards]
    .sort((left, right) => (order.get(left.slot) ?? 0) - (order.get(right.slot) ?? 0))
    .map((card) => ({
      slot: card.slot,
      definitionId: card.definitionId,
      instanceId: card.instanceId,
      level: card.level,
      cooldownRemainingMs: 0,
      activeRemainingMs: 0,
      attackAccumulatorMs: 0,
      /* E sem carga: uma sessão nova nasce com as três prontas e paradas. */
      chargeRemainingMs: 0,
      chargeStrengthBps: CARD_CHARGE_FULL_BPS,
    }));
}

/**
 * O cooldown desta carta, do CATÁLOGO e no nível congelado.
 *
 * Lido e nunca copiado: `cards.ts` decide, e a aritmética em `bigint` com uma
 * truncagem única vive lá. Uma carta sem definição devolve `0`, o que a torna
 * incapaz de activar - `cardAbilityIsImplemented` já a terá recusado antes.
 */
export function cardAbilityCooldownMs(state: CardAbilityState): number {
  const definition = cardDefinition(state.definitionId);
  if (!definition) {
    return 0;
  }
  return cardCooldownMsAtLevel(definition.baseCooldownMs, state.level);
}

/**
 * Faz correr os relógios de um espaço por UM tique.
 *
 * Os dois descem juntos, e nunca abaixo de zero. Devolve `true` quando a
 * habilidade ACABOU neste tique - a transição de activa para inactiva - para
 * quem chama poder limpar o que ela deixou.
 */
export function ageCardAbility(state: CardAbilityState, tickMs: number): boolean {
  const wasActive = state.activeRemainingMs > 0;
  if (state.cooldownRemainingMs > 0) {
    state.cooldownRemainingMs = Math.max(0, state.cooldownRemainingMs - tickMs);
  }
  if (state.chargeRemainingMs > 0) {
    state.chargeRemainingMs = Math.max(0, state.chargeRemainingMs - tickMs);
    /*
     * Uma carga que chega ao fim SOZINHA vale cheia (gate V28H.N).
     *
     * É o jogador que segurou a tecla até ao limite, e o limite é o máximo: não
     * há nada acima disto para segurar. Escrever a força aqui - e não só na
     * soltura - é o que impede um feixe automático de sair com a força da
     * activação anterior.
     */
    if (state.chargeRemainingMs === 0) {
      state.chargeStrengthBps = CARD_CHARGE_FULL_BPS;
    }
  }
  if (state.activeRemainingMs > 0) {
    state.activeRemainingMs = Math.max(0, state.activeRemainingMs - tickMs);
  }
  return wasActive && state.activeRemainingMs === 0;
}

/**
 * A carga ACABOU neste passo?
 *
 * Perguntada depois de {@link ageCardAbility}, e é o instante em que a esfera
 * sai e o feixe acende. Um instante e não um estado: quem chama tem de agir
 * exactamente uma vez, e `chargeRemainingMs === 0` sozinho é verdade para
 * sempre a partir daí.
 */
export function cardAbilityChargeEnded(before: number, after: number): boolean {
  return before > 0 && after === 0;
}

/**
 * Se este espaço pode activar AGORA.
 *
 * Quatro condições, e todas necessárias: a carta tem implementação, não está já
 * activa, o cooldown chegou a zero, e existe um alvo. A última é o §"sem alvo
 * válido, a carta permanece pronta e não consome cooldown" - uma habilidade que
 * disparasse contra um campo vazio queimaria o seu cooldown por nada.
 */
export function cardAbilityCanActivate(
  state: CardAbilityState,
  context: { readonly hasTarget: boolean },
): boolean {
  return (
    context.hasTarget &&
    cardAbilityIsImplemented(state.definitionId) &&
    state.activeRemainingMs === 0 &&
    state.cooldownRemainingMs === 0
  );
}

/**
 * Activa este espaço: liga a duração e começa o cooldown no mesmo instante.
 *
 * O acumulador é posto a zero para a primeira salva sair no tique seguinte e
 * não neste - a habilidade nasce montada, e o primeiro disparo dela é um
 * disparo e não um efeito colateral da activação.
 */
export function activateCardAbility(
  state: CardAbilityState,
  /**
   * **Quanto tempo esta carta carrega NESTA arena** (gate V28I.C).
   *
   * Obrigatório, e não lido do catálogo: a Esfera Ígnea carrega quinze segundos
   * no duelo e 400 ms na caça, e o catálogo tem zero exactamente para que
   * ninguém o leia por engano. Uma omissão aqui é um erro de compilação, que é o
   * que se quer - a alternativa era uma carta a disparar no instante da ordem
   * num dos modos, em silêncio.
   *
   * As outras quinze passam o valor do catálogo, e para elas isto é o mesmo
   * número que sempre foi.
   */
  chargeMs: number,
): void {
  const charge = Math.max(0, chargeMs);
  /*
   * A carga SOMA-SE à duração, e não a substitui.
   *
   * A Lança Solar carrega 1,5 s e arde 1,2 s: a habilidade está ligada durante
   * os 2,7 s, e o que muda a meio é o que ela faz. Guardar só a duração faria a
   * carta acabar antes de o feixe acender; guardar só a carga faria o feixe não
   * ter tempo nenhum.
   *
   * O arrefecimento começa aqui para quase todas - ele conta desde a ordem - e
   * a excepção está escrita mais abaixo.
   */
  state.chargeRemainingMs = charge;
  /*
   * A força ainda NÃO está decidida, e é isso que o valor cheio significa
   * enquanto `chargeRemainingMs` corre: uma carta que não carrega vale cheia
   * desde já, e uma que carrega escreve a sua no instante em que a solta.
   */
  state.chargeStrengthBps = CARD_CHARGE_FULL_BPS;
  state.activeRemainingMs = charge + cardAbilityDurationMs(state.definitionId);
  /*
   * **A esfera não gasta arrefecimento enquanto CARREGA** (gate V28I.C).
   *
   * Decisão do dono: "o cooldown começa somente quando o projéctil é
   * efectivamente disparado, não quando a carga começa". Com quinze segundos de
   * carga e 8,8 s de arrefecimento ao nível 6, contar desde a ordem punha a
   * carta pronta outra vez seis segundos antes de ela sequer disparar - o
   * arrefecimento deixava de significar alguma coisa.
   *
   * Só ela: a Lança Solar acende no fim da carga dela e continua a contar desde
   * a ordem, e as catorze que não carregam acontecem no instante em que são
   * ligadas. Quem dispara a esfera chama {@link cardBeginCooldown}.
   */
  state.cooldownRemainingMs =
    cardBalance(state.definitionId)?.effect === 'charged_projectile'
      ? 0
      : cardAbilityCooldownMs(state);
  state.attackAccumulatorMs = 0;
}

/**
 * **Começa o arrefecimento AGORA** (gate V28I.C).
 *
 * Para a carta cujo arrefecimento não conta desde a ordem, mas desde o disparo.
 * Chamada pelos dois modos no mesmo sítio - o instante em que a esfera sai - e
 * é ela que faz "quinze segundos de carga mais 8,8 de espera" ser o preço real
 * da carta, em vez de os dois relógios correrem sobrepostos.
 *
 * Idempotente para o que já está a arrefecer: escrever o mesmo número duas vezes
 * é escrever o mesmo número.
 */
export function cardBeginCooldown(state: CardAbilityState): void {
  state.cooldownRemainingMs = cardAbilityCooldownMs(state);
}

/** Está a CARREGAR neste instante: ligada, e ainda sem produzir nada. */
export function cardAbilityIsCharging(state: CardAbilityState): boolean {
  return state.chargeRemainingMs > 0;
}

/**
 * Está a produzir efeito neste instante: ligada, e com a carga feita.
 *
 * A pergunta que a Lança Solar e a Esfera Ígnea obrigaram a separar de
 * {@link cardAbilityIsActive}. Uma carta sem carga responde o mesmo às duas.
 */
export function cardAbilityIsFiring(state: CardAbilityState): boolean {
  return state.activeRemainingMs > 0 && state.chargeRemainingMs === 0;
}

/** Se este espaço está a produzir efeito neste instante. */
export function cardAbilityIsActive(state: CardAbilityState): boolean {
  return state.activeRemainingMs > 0;
}

/**
 * **Solta a carga deste espaço** (gate V28H.N).
 *
 * ## O contrato que esta função restaura
 *
 * "A habilidade é carregada enquanto o botão ou tecla permanece pressionado."
 * Uma carga de duração fixa - que foi o que esteve escrito por engano - dispara
 * sozinha ao fim de um tempo e tira ao jogador a única decisão que a carta tem.
 *
 * ## O que ela decide, e o que não decide
 *
 * Fixa a FORÇA a partir do que foi aguentado, apaga o resto da carga e encolhe o
 * tempo activo para a duração do efeito - o feixe começa AGORA, e não daqui a
 * meio segundo. Não toca no arrefecimento: ele conta desde a ordem, como sempre,
 * e soltar cedo não devolve nada.
 *
 * Devolve `false` quando não havia carga nenhuma para soltar, e quem chama
 * transforma isso numa recusa em vez de num disparo vindo do nada.
 */
export function releaseCardCharge(state: CardAbilityState, chargeMs: number): boolean {
  if (state.activeRemainingMs <= 0 || state.chargeRemainingMs <= 0) {
    return false;
  }
  /* O tempo TOTAL desta arena, que é sobre o que a força é medida (V28I.C). */
  const charge = Math.max(0, chargeMs);
  state.chargeStrengthBps = cardChargeStrengthBps(charge, state.chargeRemainingMs);
  state.chargeRemainingMs = 0;
  /*
   * O tempo activo passa a ser SÓ a duração.
   *
   * Ele nasceu como carga mais duração; a carga que sobrava não foi cumprida,
   * portanto deixá-la lá daria ao feixe um tempo de queima que ninguém pagou.
   */
  state.activeRemainingMs = cardAbilityDurationMs(state.definitionId);
  state.attackAccumulatorMs = 0;
  return true;
}

/** Se este espaço tem uma carga por soltar neste instante. */
export function cardAbilityCanRelease(state: CardAbilityState): boolean {
  return state.activeRemainingMs > 0 && state.chargeRemainingMs > 0;
}

/**
 * **As duas explosões de CARTA têm arte própria** (gate V28H.T).
 *
 * Elas partilhavam a da Bomba Atômica, e o resultado foi o que o dono viu: duas
 * cartas diferentes com a mesma explosão, e o Catolé - que tem **seis vezes** o
 * raio da Bomba - a ler-se igual a uma esfera de fogo.
 *
 * A cena escolhe pelo id e nunca pelo raio: adivinhar pela medida seria decidir
 * a arte a partir de um número de equilíbrio, e o dia em que uma esfera tivesse
 * o raio de um Catolé seria o dia em que ela ganhava a explosão dele.
 *
 * Aqui e não em `damage/origin.ts`: aquele ficheiro nomeia o que uma JOIA emite,
 * e o seu tipo é fechado sobre isso de propósito. Uma carta não é uma joia.
 */
export const FIREBALL_BLAST_EFFECT = 'card_fireball_blast';
export const CATOLE_BLAST_EFFECT = 'card_catole_blast';
